import { ComponentIds, Entity, OutlinedComponent, ProductComponent } from '@inter-ikea-core/core';
import {
  CoreApi,
  Movement,
  OutlineColor,
  PlannerProductComponent,
  SelectionOverrideComponent,
  moveEntity as frameworkMoveEntity
} from '@inter-ikea-storageone/range';
import { CornerSolutionComponent } from '@inter-ikea-storageone/range';
import { useCore } from 'contexts/core';
import { getProductEntities } from 'ducks/core';
import { isDoorsVisible } from 'ducks/layout';
import { getMarketProducts } from 'ducks/products';
import { getTranslatedTitle } from 'products';
import { getFrameSlidingDoor } from 'rangeConfig/pax/utils/utils';
import { useCallback, useEffect, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

const PositionWrapper = styled.div`
  position: absolute;
  top: ${({ theme }) => `calc(-${theme.space.xl} / 2)`};
  left: ${({ theme }) => `calc(-${theme.space.xl} / 2)`};
  margin-left: -15px;
  margin-top: -15px;
`;

const TabButton = styled.button`
  opacity: 0;
  pointer-events: none;
`;

type EntityWithPosition = {
  entity: Entity;
  position: { x: number; y: number };
  enabled: boolean;
};

export const MoveEntity = (coreApi: CoreApi, movement: Movement) => {
  const selectedEntity = coreApi.entities.findSelectedEntity();
  if (!selectedEntity) return;
  const cameraTransform = coreApi.camera.getCameraTransform();
  const cameraDirection = coreApi.camera.getCameraDirection(movement);
  frameworkMoveEntity(selectedEntity, 50, cameraDirection, cameraTransform);
};

export default function SceneEntityTabButtons() {
  const coreApi = useCore();
  const [tabButtons, setTabButtons] = useState<EntityWithPosition[]>([]);
  const productsInScene = useSelector(getProductEntities);
  const products = useSelector(getMarketProducts);
  const doorsVisible = useSelector(isDoorsVisible);
  const { formatMessage } = useIntl();

  const sortByXPosition = useCallback(
    (a: Entity, b: Entity) => {
      const aPosition = coreApi.entities.getWithPosition(a)?.position;
      const bPosition = coreApi.entities.getWithPosition(b)?.position;
      if (!aPosition || !bPosition) {
        return 0;
      }
      return aPosition.x === bPosition.x ? aPosition.y - bPosition.y : aPosition.x - bPosition.x;
    },
    [coreApi.entities]
  );

  const sortFrameChildren = useCallback(
    (a: Entity, b: Entity) => {
      const aPosition = coreApi.entities.getWithPosition(a)?.position;
      const bPosition = coreApi.entities.getWithPosition(b)?.position;
      if (!aPosition || !bPosition) {
        return 0;
      }
      return aPosition.y === bPosition.y ? aPosition.x - bPosition.x : aPosition.y - bPosition.y;
    },
    [coreApi.entities]
  );

  const sortEntitiesByPosition = useCallback(
    (entitiesInScene: Entity[]) => {
      const frames = entitiesInScene
        .filter(
          entity =>
            entity.hasComponent(ComponentIds.FRAME_COMPONENT_ID) ||
            entity.hasComponent(CornerSolutionComponent.id)
        )
        .sort(sortByXPosition);
      const sortedEntities = frames.reduce((acc: Entity[], frame: Entity) => {
        const frameChildren = getFrameEntities(frame, coreApi);
        const unfilteredFrameDoors = getFrameDoors(frame, coreApi);
        const frameDoors = filterDoorsInList(acc, unfilteredFrameDoors);

        let newAcc = [...acc];

        if (doorsVisible && unfilteredFrameDoors.length > 0) {
          newAcc = [...newAcc, ...frameDoors];
        } else {
          newAcc = [...newAcc, frame, ...frameChildren.sort(sortFrameChildren)];
        }

        return newAcc;
      }, []);
      return sortedEntities;
    },
    [coreApi, doorsVisible, sortByXPosition, sortFrameChildren]
  );

  /**
   * Only return doors that are not already added to the list. Sliding doors
   * can span over several frames and must therefore be filtered.
   * @param entites list of entities to be represented with a tab button
   * @param doors a frames doors
   * @returns
   */
  const filterDoorsInList = (entites: Entity[], doors: Entity[]): Entity[] => {
    return doors.filter(door => !entites.some(e => e.id === door.id));
  };

  const getFrameEntities = (frame: Entity, coreApi: CoreApi): Entity[] => {
    return coreApi.entities
      .getFrameDescendants(frame)
      .filter(
        child =>
          child.hasComponent(PlannerProductComponent.id) &&
          !child.hasComponent(ComponentIds.DOOR_COMPONENT_ID) &&
          !child.hasComponent(ComponentIds.HANDLE_COMPONENT_ID)
      );
  };

  const getFrameDoors = (frame: Entity, coreApi: CoreApi) => {
    let doors = coreApi.entities
      .getFrameDescendants(frame)
      .filter(child => child.hasComponent(ComponentIds.DOOR_COMPONENT_ID));

    const slidingDoor = getFrameSlidingDoor(frame);

    if (slidingDoor) {
      doors = [...doors, slidingDoor];
    }

    return doors;
  };

  const updateTabButtons = useCallback(() => {
    const entities = coreApi.entities.getByComponent(PlannerProductComponent.id);
    const tabButtonsEnabled = coreApi.entities.findSelectedEntity() === null;
    const sortedEntities = sortEntitiesByPosition(entities);

    const selectOverideEntities = coreApi.entities.getByComponent(SelectionOverrideComponent.id);

    // STOR-6816 If we are in interior suggestion tab in interior menu make only the dots tabbable, not the entire scene
    if (selectOverideEntities.length > 0 && tabButtonsEnabled) {
      setTabButtons([]);
      return;
    }

    const entitiesWithPosition = sortedEntities.reduce(
      (acc: EntityWithPosition[], entity: Entity) => {
        const position = coreApi.entities.getWithPosition(entity)?.position;
        return position
          ? [
              ...acc,
              {
                entity,
                position,
                enabled: tabButtonsEnabled
              }
            ]
          : acc;
      },
      []
    );
    setTabButtons(entitiesWithPosition);
  }, [coreApi.entities, sortEntitiesByPosition]);

  useEffect(() => {
    updateTabButtons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productsInScene, doorsVisible]);

  const getToolTipText = (entity: Entity) => {
    const productRef = entity.getComponent<ProductComponent>(ComponentIds.PRODUCT_COMPONENT_ID)
      ?.productRef;
    const product = products.find(p => p.coreProductId === productRef);
    if (!product) return '';
    return getTranslatedTitle(
      'storageone.paxGuideCustomizeButton typeName',
      product,
      formatMessage
    );
  };

  const entitySelected = useCallback(() => {
    updateTabButtons();
  }, [updateTabButtons]);

  const entityUnselected = useCallback(() => {
    updateTabButtons();
  }, [updateTabButtons]);

  useEffect(() => {
    coreApi
      .getServices()
      .entityManager.subscribeAdd(ComponentIds.SELECTED_COMPONENT_ID, entitySelected);
    coreApi
      .getServices()
      .entityManager.subscribeRemove(ComponentIds.SELECTED_COMPONENT_ID, entityUnselected);
    return () => {
      coreApi
        .getServices()
        .entityManager.unsubscribeAdd(ComponentIds.SELECTED_COMPONENT_ID, entitySelected);
      coreApi
        .getServices()
        .entityManager.unsubscribeRemove(ComponentIds.SELECTED_COMPONENT_ID, entityUnselected);
    };
  }, [coreApi, entitySelected, entityUnselected]);

  const handleClick = (entity: Entity) => {
    coreApi.scene?.setGlobalOutlineColor(OutlineColor.BLUE);
    if (entity.id === coreApi.entities.findSelectedEntity()?.id) {
      handleEscClicked();
      return;
    }
    coreApi.entities.select(entity);
  };

  const handleEscClicked = () => {
    const selectedEntity = coreApi.entities.findSelectedEntity();
    if (!selectedEntity) return;
    coreApi.entities.unselect();
    document.getElementById(selectedEntity.id.toString())?.focus();
    handleFocus(selectedEntity);
  };

  const handleFocus = (entity: Entity) => {
    coreApi.scene?.setGlobalOutlineColor(OutlineColor.BLACK);
    entity.addComponent(new OutlinedComponent());
  };

  const handleFocusLost = (entity: Entity) => {
    coreApi.scene?.setGlobalOutlineColor(OutlineColor.BLUE);
    if (!entity?.hasComponent(ComponentIds.SELECTED_COMPONENT_ID)) {
      entity?.removeComponent(ComponentIds.OUTLINED_COMPONENT_ID);
    }
  };

  useHotkeys('left', () => MoveEntity(coreApi, Movement.RIGHT));
  useHotkeys('right', () => MoveEntity(coreApi, Movement.LEFT));
  useHotkeys('up', () => MoveEntity(coreApi, Movement.UP));
  useHotkeys('down', () => MoveEntity(coreApi, Movement.DOWN));
  useHotkeys('esc', handleEscClicked);

  useEffect(() => {
    return coreApi.subscribe('update', updateTabButtons);
  }, [coreApi, updateTabButtons]);

  return tabButtons.map(({ entity, position, enabled }: EntityWithPosition) => (
    <PositionWrapper
      key={entity.id}
      style={{
        left: position.x + `px`,
        top: position.y + `px`
      }}
    >
      <TabButton
        id={entity.id.toString()}
        onClick={() => handleClick(entity)}
        onFocus={() => handleFocus(entity)}
        onBlur={() => handleFocusLost(entity)}
        tabIndex={enabled ? 0 : -1}
        title={getToolTipText(entity)}
      />
    </PositionWrapper>
  ));
}
